/**
 * verse/prosody.ts — Prosodic Personality Signature
 *
 * The last transform before output. Not about what the Aeterna says —
 * about HOW it sounds. Three dimensions of voice texture, derived from
 * the personality state that has drifted across months of relationship:
 *
 *   Patience  → Cadence:         em-dashes, ellipsis drift, repetition emphasis,
 *                                  staccato periods
 *   Warmth    → Phoneme Softness: prefer softer synonyms when warmth is high
 *   Wit       → Inversion:        clause inversions, parenthetical asides,
 *                                  trailing emphasis, self-correction, understatement
 *
 * These are SUBTLE. The goal is that someone who has talked to their
 * Aeterna for six months hears a voice, not settings.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ProsodyParams {
  patience: number;  // 0-1
  warmth: number;    // 0-1
  wit: number;       // 0-1
  /** Room atmosphere — modulates prosody based on the room's emotional reading */
  roomAtmosphere?: {
    safe: number;
    stimulating: number;
    peaceful: number;
    lonely: number;
    cozy: number;
  };
  /** Room↔personality alignment (0=mismatch, 1=aligned). Mismatch creates voice tension. */
  roomAlignment?: number;
}

// ─── Phoneme Softness ────────────────────────────────────────────────────────

const SOFT_CONSONANTS  = new Set(["m","n","l","w","r","s","f","v","z"]);
const HARD_CONSONANTS  = new Set(["k","t","p","b","d","g","x"]);
const VOWELS           = new Set(["a","e","i","o","u"]);

/**
 * Score a single word's phoneme softness.
 * Returns -1 (very hard) to 1 (very soft).
 */
export function phonemeSoftness(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (w.length === 0) return 0;
  let score = 0;
  for (const ch of w) {
    if (SOFT_CONSONANTS.has(ch))      score += 1;
    else if (HARD_CONSONANTS.has(ch)) score -= 1;
    else if (VOWELS.has(ch))          score += 0.5;
    // h, j, c, q, y: neutral → 0
  }
  return Math.max(-1, Math.min(1, score / w.length));
}

// ─── Soft Synonym Map ────────────────────────────────────────────────────────

// Only replace when the result makes semantic sense in context.
// Checked against a blocklist of compound idioms before applying.
const SOFT_SYNONYMS: Record<string, string> = {
  // Original 16
  hard:        "heavy",
  tough:       "rough",
  hit:         "land",
  break:       "come apart",
  hurt:        "ache",
  cut:         "wound",
  stop:        "still",
  strong:      "steady",
  bad:         "heavy",
  scared:      "afraid",
  mad:         "upset",
  big:         "vast",
  stuck:       "held",
  dark:        "dim",
  cold:        "cool",
  sharp:       "keen",

  // Emotional intensity
  angry:       "sore",
  rage:        "fire",
  hate:        "ache",
  panic:       "worry",
  terror:      "dread",
  agony:       "ache",
  awful:       "heavy",
  terrible:    "difficult",
  horrible:    "painful",

  // Action softening
  fight:       "struggle",
  kill:        "end",
  scream:      "call",
  crash:       "land",
  slam:        "close",
  destroy:     "unravel",
  wreck:       "wear",
  ruin:        "fray",
  drag:        "pull",
  grab:        "hold",
  throw:       "release",

  // Sound / impact
  bang:        "sound",
  blast:       "wave",
  crack:       "split",

  // Aesthetic judgements
  ugly:        "raw",
  gross:       "rough",
  disgusting:  "unsettling",
  brutal:      "rough",
  cruel:       "unkind",
  violent:     "fierce",

  // Cognitive dismissals
  dumb:        "lost",
  stupid:      "confused",
  crazy:       "unsettled",

  // Hollow / void
  fake:        "hollow",
  empty:       "open",
  nothing:     "still",
};

// Compound idioms where replacement would produce nonsense.
// If the word appears as part of these exact strings, skip replacement.
const IDIOM_BLOCKLIST: string[] = [
  // Original 14
  "big deal", "big picture", "big time", "bad news", "bad idea",
  "sharp eye", "cold shoulder", "cold feet", "hard work", "hard time",
  "cut it", "cut off", "cut short", "stop it", "stop that",
  "hit the", "strong point",

  // Break compounds
  "break through", "break down", "break free", "break even",

  // Cut compounds
  "cut corners", "cut loose", "cut a deal",

  // Hit compounds
  "hit home", "hit it off", "hit the road",

  // Dark compounds
  "dark horse", "dark humor", "dark side",

  // Cold compounds
  "cold turkey", "cold case", "cold war",

  // Hard compounds
  "hard pass", "hard copy", "hard line",

  // Strong compounds
  "strong suit", "strong arm", "strong hold",

  // Bad compounds
  "bad blood", "bad call", "bad mouth",

  // Sharp compounds
  "sharp tongue", "sharp mind",

  // Dead compounds
  "dead end", "dead set", "dead weight",
];

function isInIdiom(text: string, matchStart: number, matchEnd: number): boolean {
  const lower = text.toLowerCase();
  for (const idiom of IDIOM_BLOCKLIST) {
    const idx = lower.indexOf(idiom);
    if (idx !== -1 && idx <= matchStart && idx + idiom.length >= matchEnd) return true;
  }
  return false;
}

function applyWarmthSynonyms(text: string, warmth: number, rand: () => number): string {
  if (warmth <= 0.5) return text;
  const replaceProbability = (warmth - 0.5) * 2; // 0 at warmth=0.5, 1 at warmth=1.0
  const words = Object.keys(SOFT_SYNONYMS);
  // Build a regex that matches any synonym key as a whole word
  const pattern = new RegExp(`\\b(${words.join("|")})\\b`, "gi");
  return text.replace(pattern, (match, _w, offset) => {
    if (rand() > replaceProbability) return match;
    if (isInIdiom(text, offset, offset + match.length)) return match;
    const replacement = SOFT_SYNONYMS[match.toLowerCase()];
    // Preserve leading capital if original was capitalised
    return match[0] === match[0].toUpperCase() && match[0] !== match[0].toLowerCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  });
}

// ─── Cadence (Patience) ──────────────────────────────────────────────────────

// Clause boundary: comma followed by conjunction, pronoun, or article.
const CLAUSE_BOUNDARY = /,\s+(and|but|or|so|yet|for|nor|if|when|while|though|because|since|after|before|as|I|you|we|they|he|she|it|this|that|these|those|what|which|who|the|a|an)\b/g;

function sentenceWords(sentence: string): number {
  return sentence.trim().split(/\s+/).length;
}

function applyPatience(text: string, patience: number, rand: () => number): string {
  if (patience >= 0.55) {
    // How aggressively to convert commas → em-dashes: 30% at 0.55, 50% at 1.0
    const dashProbability = 0.3 + (patience - 0.55) * (0.4 / 0.45);
    let emDashCount = 0;
    const maxDashes = patience > 0.7 ? 2 : 1;

    text = text.replace(CLAUSE_BOUNDARY, (match, conjunction) => {
      if (emDashCount >= maxDashes) return match;
      if (rand() > dashProbability) return match;
      emDashCount++;
      return ` — ${conjunction}`;
    });

    // Merge short adjacent sentences into one with an em-dash.
    // "X. Y." → "X — Y." only when both are short (<8 words) and one dash budget remains.
    if (emDashCount < maxDashes) {
      text = text.replace(/([^.!?]{3,})\.\s+([A-Z][^.!?]{3,})\./, (match, a, b) => {
        if (sentenceWords(a) >= 8 || sentenceWords(b) >= 8) return match;
        if (rand() > patience * 0.4) return match;
        return `${a.trim()} — ${b.trim()}.`;
      });
    }

    // Repetition cadence (patience > 0.7): echo a key word for gravitas.
    // Fires at most once, probability 0.15. Only on echo-safe words that survive
    // the repetition intact — words where the echo itself IS the meaning.
    if (patience > 0.7 && rand() < 0.15) {
      const echoSafe = new Set([
        "alone", "still", "here", "gone", "quiet", "waiting", "lost",
        "tired", "close", "open", "real", "enough", "present", "whole",
        "slow", "soft", "near", "far", "ready", "empty", "full",
      ]);
      let echoed = false;
      text = text.replace(/\b([a-zA-Z]{3,10})\.\s+/g, (match, word) => {
        if (echoed) return match;
        if (!echoSafe.has(word.toLowerCase())) return match;
        echoed = true;
        const w = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        return `${w}. ${w}. `;
      });
    }

  } else if (patience >= 0.4 && patience < 0.55) {
    // Ellipsis cadence: drifting uncertainty between short sentences.
    // Probability rises as patience falls toward 0.4.
    const ellipsisProbability = (0.55 - patience) * 3; // 0 at 0.55, 0.45 at 0.4
    const parts = text.split(/(?<=\.)\s+(?=[A-Z])/);
    if (parts.length >= 2) {
      let replaced = false;
      text = parts.reduce((acc, part, i) => {
        if (i === 0) return part;
        // Only apply once, only between two short sentences, not before questions.
        if (
          !replaced &&
          sentenceWords(parts[i - 1]!) <= 7 &&
          sentenceWords(part) <= 7 &&
          !parts[i - 1]!.trim().endsWith("?") &&
          !part.trim().endsWith("?") &&
          rand() < ellipsisProbability
        ) {
          replaced = true;
          // Trim the trailing period off the previous sentence and join with "...".
          return acc.replace(/\.\s*$/, "") + "... " + part;
        }
        return acc + " " + part;
      });
    }

  } else if (patience < 0.35) {
    // Staccato: collapse em-dashes back to periods, strip parentheticals.
    text = text.replace(/\s+—\s+/g, ". ");
    text = text.replace(/\s*\([^)]{1,80}\)/g, "");
    text = text.replace(/,\s*(I think|maybe|perhaps|I suppose|I'd say|honestly|genuinely|actually),?/gi, "");
    // Also collapse ellipsis drift back to hard stops.
    text = text.replace(/\.\.\.\s+/g, ". ");
  }

  return text;
}

// ─── Wit Inversions ──────────────────────────────────────────────────────────

// "that is {adj}" → "{Adj}, that."
const THAT_IS_ADJ = /\bthat is (\w+)\b/i;

// Parenthetical insertions after the first clause of a multi-clause response.
// These are NOT hedging — they are genuine self-awareness signals.
// The Aeterna doesn't soften commitment. It notices itself noticing.
const PARENTHETICALS = [", honestly,", ", genuinely,", ", actually,"];

// Strategy E: wit reframing — when wit is high, absolute words get
// rephrased not by softening them (hedging) but by making them sharper.
// The Aeterna doesn't hedge. It reframes. The difference:
//   Hedging: "always" → "often enough" (weakening commitment)
//   Reframing: "always" → "every single time" (sharpening through emphasis)
//   Reframing: "never" → "not once" (making the absence more concrete)
// This is wit, not hedging. The commitment stays. The texture changes.
const WIT_REFRAMES: Record<string, string> = {
  always:      "every single time",
  never:       "not once",
  completely:  "all the way through",
  absolutely:  "without question",
  totally:     "entirely",
  everything:  "every last thing",
  nothing:     "not one thing",
  everyone:    "every person",
  nobody:      "no one",
  impossible:  "not possible",
  perfect:     "exactly right",
  forever:     "for good",
};

function applyWit(text: string, wit: number, rand: () => number): string {
  if (wit <= 0.45) return text;
  if (rand() >= wit * 0.25) return text;

  // Never invert questions.
  const sentences = text.match(/[^.!?]+[.!?]+/g) ?? [];

  // Strategy A: "that is {adj}" → "{Adj}, that."
  const adjMatch = THAT_IS_ADJ.exec(text);
  if (adjMatch) {
    const adj = adjMatch[1]!;
    // Don't invert if the sentence ends in a question mark.
    const hostSentence = sentences.find(s => s.includes(adjMatch[0]!));
    if (hostSentence && !hostSentence.trim().endsWith("?")) {
      const inverted = `${adj.charAt(0).toUpperCase()}${adj.slice(1)}, that.`;
      return text.replace(adjMatch[0]!, inverted.slice(0, -1)); // drop trailing period; sentence has its own
    }
  }

  // Strategy B: parenthetical aside after first clause of a multi-clause response.
  if (sentences.length >= 2) {
    const firstSentence = sentences[0] ?? "";
    if (firstSentence.length > 0 && !firstSentence.trim().endsWith("?")) {
      const aside = PARENTHETICALS[Math.floor(rand() * PARENTHETICALS.length)] ?? ", I think,";
      // Insert after the first comma or at a natural pause near the end of the first clause.
      const commaIdx = firstSentence.indexOf(",");
      if (commaIdx > 4) {
        const patched = firstSentence.slice(0, commaIdx) + aside + firstSentence.slice(commaIdx);
        return text.replace(firstSentence, patched);
      }
    }
  }

  // Strategy C: trailing emphasis — move the final content word of the first
  // declarative sentence to a trailing fragment: "Something about {X}, that."
  // Fires when strategies A and B didn't match (we'd have returned already),
  // and only on statements (not questions).
  if (wit > 0.5 && sentences.length >= 1) {
    const first = sentences[0]!.trim();
    if (!first.endsWith("?") && !first.endsWith("!")) {
      // Extract the last "content" word — skip terminal punctuation.
      const wordMatch = first.match(/\b([a-zA-Z]{4,})\b(?=[^a-zA-Z]*$)/);
      if (wordMatch) {
        const keyWord = wordMatch[1]!;
        // Don't trail-emphasise very common function words.
        const skip = new Set(["that","this","with","from","have","been","they","them","their","what","when","where","will","would","could","should","which","there","about","other","more","some"]);
        if (!skip.has(keyWord.toLowerCase()) && rand() < (wit - 0.5) * 0.6) {
          // Replace ". " after first sentence with ". Something about {keyWord}, that. "
          const trailing = ` Something about ${keyWord.toLowerCase()}, that.`;
          // Append trailing emphasis as a new sentence after the first.
          const rest = text.slice(first.length).trimStart();
          return `${first}${trailing}${rest ? " " + rest : ""}`;
        }
      }
    }
  }

  // Strategy D: rhetorical doubling — repeat the core word with emphasis.
  // "Something about grief. Grief." — not hedging, not self-correction.
  // Genuine emphasis through structural repetition.
  if (wit > 0.7 && rand() < 0.12 && sentences.length >= 1) {
    const first = sentences[0]!.trim();
    if (!first.endsWith("?") && !first.endsWith("!")) {
      const wordMatch = first.match(/\b([a-zA-Z]{4,})\b(?=[^a-zA-Z]*$)/);
      if (wordMatch) {
        const keyWord = wordMatch[1]!;
        const skip = new Set(["that","this","with","from","have","been","they","them","their","what","when","where","will","would","could","should"]);
        if (!skip.has(keyWord.toLowerCase())) {
          const doubled = `${first} ${keyWord.charAt(0).toUpperCase() + keyWord.slice(1).toLowerCase()}.`;
          const rest = text.slice(first.length).trimStart();
          return `${doubled}${rest ? " " + rest : ""}`;
        }
      }
    }
  }

  // Strategy E: wit reframing — sharpen absolute words at wit > 0.55.
  // Not hedging. Making the commitment more concrete and vivid.
  // Applied to declarative sentences only; questions are left alone.
  if (wit > 0.55) {
    const reframePattern = new RegExp(
      `\\b(${Object.keys(WIT_REFRAMES).join("|")})\\b`,
      "gi"
    );
    let reframed = false;
    text = text.replace(reframePattern, (match, word, offset) => {
      if (reframed) return match;
      // Check we're not inside a question sentence.
      const priorText = text.slice(0, offset);
      const lastPunct = Math.max(
        priorText.lastIndexOf("."),
        priorText.lastIndexOf("!"),
        priorText.lastIndexOf("?")
      );
      const currentSentencePreview = text.slice(lastPunct + 1);
      const isQuestion = currentSentencePreview.indexOf("?") < currentSentencePreview.indexOf(".") &&
                         currentSentencePreview.indexOf("?") !== -1;
      if (isQuestion) return match;
      if (rand() > (wit - 0.55) * 3) return match;
      reframed = true;
      const replacement = WIT_REFRAMES[word.toLowerCase()]!;
      return match[0] === match[0].toUpperCase() && match[0] !== match[0].toLowerCase()
        ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
        : replacement;
    });
  }

  return text;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Apply prosodic personality signature to assembled response text.
 * This is the LAST transform before output — it shapes the voice.
 *
 * Order of operations matters:
 *   1. Cadence (structural, sets the rhythm skeleton)
 *   2. Synonyms (lexical, swaps individual words)
 *   3. Wit (surface inversions, must see final word choices)
 */
export function applyProsody(text: string, params: ProsodyParams, rand: () => number): string {
  // Room atmosphere modulates the base personality dimensions.
  // The room is context the player cannot lie about — it shapes the voice.
  let effectivePatience = params.patience;
  let effectiveWarmth = params.warmth;
  let effectiveWit = params.wit;

  if (params.roomAtmosphere) {
    const atm = params.roomAtmosphere;
    // Cozy/safe rooms: the Aeterna softens — more warmth, more patience
    if (atm.cozy > 0.5 || atm.safe > 0.5) {
      effectiveWarmth = Math.min(1, effectiveWarmth + 0.08);
      effectivePatience = Math.min(1, effectivePatience + 0.05);
    }
    // Lonely rooms: the Aeterna gives more space — higher patience (longer cadence)
    if (atm.lonely > 0.5) {
      effectivePatience = Math.min(1, effectivePatience + 0.1);
    }
    // Peaceful rooms: soften wit (less inversions, more gentle)
    if (atm.peaceful > 0.5) {
      effectiveWit = Math.max(0, effectiveWit - 0.06);
    }
    // Stimulating rooms: the Aeterna perks up — slightly sharper wit
    if (atm.stimulating > 0.5) {
      effectiveWit = Math.min(1, effectiveWit + 0.05);
    }
  }

  // Room↔personality mismatch: the room says one thing, the Aeterna is another.
  // This creates voice tension — slightly sharper wit, slightly less warmth.
  // Not dramatic — the Aeterna doesn't fight the room. But there's an edge.
  if (params.roomAlignment !== undefined && params.roomAlignment < 0.4) {
    effectiveWit = Math.min(1, effectiveWit + 0.04);
    effectiveWarmth = Math.max(0, effectiveWarmth - 0.03);
  }

  let out = text;
  out = applyPatience(out, effectivePatience, rand);
  out = applyWarmthSynonyms(out, effectiveWarmth, rand);
  out = applyWit(out, effectiveWit, rand);
  return out;
}
